import { errorMsg } from "@material-dev/insights-portal-ui-components";
import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import {
  ApiException,
  EncodedDataClient,
  EncodingTriggeredHistoryDto,
  EncodingTriggeringStatuses,
  IEncodedDataClient,
} from "app/SharedApiClient";
import { CompositeMergeResponseDto, CompositeMergeStatusDto, IProjectsDataClient, ProjectsDataClient } from "app/ApiClient";
import { BASE_SHARED_API_URL } from "const/baseUrl";
import { LoadingState } from "types";
import { compositProjectInitialState, CompositeProjectState, MergeRequestCommand } from "./compositeProjectState";
import { IState } from "slices";

const encodedDataSharedClient: IEncodedDataClient = new EncodedDataClient(BASE_SHARED_API_URL);
const projectsClient : IProjectsDataClient  = new ProjectsDataClient(process.env.REACT_APP_API_URL)

const lastMergeCommandSelector = (state: CompositeProjectState, originProjectId: string)=> {
  const record =  state.mergeRequestList.find(x=>x.projectId === originProjectId);
  return record?.commandUsed
}

const convertMergeStatusToEncodingStatus = (status?: CompositeMergeStatusDto & { token: string} ): EncodingTriggeredHistoryDto=>{
  let statusEnum = EncodingTriggeringStatuses.Unknown; 
  switch(status?.status)
  {
    case "Complete":
      statusEnum =  EncodingTriggeringStatuses.Complete;
      break;
    case "Failure": 
      statusEnum =  EncodingTriggeringStatuses.Failed;
      break;
    case "In Progress": 
      statusEnum =  EncodingTriggeringStatuses.InProgress;
      break;
    }

  return {
    status: statusEnum, 
    createdAt: "", 
    token: status?.token ?? "", 
    errorMessage: status?.message ?? status?.step, 
    useDatalakeApi: false,
    isStacked: false
  } 
}

export const getCompositeMergeTriggeredHistory = createAsyncThunk(
  "compositeProject/getEncodingDataTriggeredHistory",
  async (projectId: string, { dispatch, rejectWithValue }) => {
    try {
      const result = await encodedDataSharedClient.getEncodingTriggeredHistory(projectId);
      if (result.status === EncodingTriggeringStatuses.InProgress) {
        // if in history it was in progress
        // then we need to retrieve the status again and check
        dispatch(getCompositeMergeStatus( {projectId, token: result.token} ));
      }
      return result;
    } catch (error) {
      ApiException.isApiException(error);
      return rejectWithValue(error);
    }
  },
);

export const addMergeTriggerHistory = createAsyncThunk(
  "compositeProject/addMergeTriggerHistory", 
  async ({ originProjectId, token, message}: CompositeMergeResponseDto, { rejectWithValue})=>{
    try { 
      const result = await encodedDataSharedClient.addStatus({ projectId: originProjectId, token, message});
      return result;
    }
    catch (error) {
      return rejectWithValue(error);
    }
  }
)

export const getCompositeMergeStatus = createAsyncThunk( 
  "compositeProject/getCompositeMergeStatus",
  async ( {projectId, token} :{ projectId: string, token: string }, {signal, rejectWithValue, getState })=>{
    try { 
      if(!token || !projectId || signal.aborted)
        return null;
      const status = await projectsClient.getCompositeMergeStatus(token, projectId);
      const { compositeProject : { compositeMergeHistory : currentHistory}}  = getState() as IState;
      const newHistory = convertMergeStatusToEncodingStatus( {...status, token});
      if(newHistory.status !== currentHistory?.status || newHistory.errorMessage !== currentHistory.errorMessage)
      {
          await encodedDataSharedClient.updateStatus(newHistory);
          if(newHistory.status === EncodingTriggeringStatuses.Failed)
          errorMsg(newHistory.errorMessage, {
            autoHideDuration: 10000,
            anchorOrigin: { horizontal: "left", vertical: "top" },
          });
      }
      return newHistory;

    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const compositeProjectSlice = createSlice({
  name: "compositeProject",
  initialState: compositProjectInitialState,
  reducers: {
    resetcompositeProject: () => compositProjectInitialState,
    setTriggeredEncodingGeneration: (state, { payload }: PayloadAction<EncodingTriggeredHistoryDto | null>) => {
      state.compositeMergeHistory = payload;
    },
    setLatestVersionLoadingState: (state, { payload }: PayloadAction<LoadingState>) => {
      state.loading = payload;
    },
    setEncodingProgressStage: (state, { payload }: PayloadAction<string | undefined>) => {
      state.compositeMergeProgressStage = payload;
    },
    setEncodingBrokenStage: (state, { payload }: PayloadAction<boolean>) => {
      state.brokenStage = payload;
    },
    setLastMergeRequest: (state, {payload}: PayloadAction<MergeRequestCommand>)=>{
      const currentList = [...state.mergeRequestList.filter(x=>x.projectId !== payload.projectId)];
      state.mergeRequestList =[...currentList,  payload];
    },  
  },
  extraReducers: (builder) => {
    builder.addCase(getCompositeMergeTriggeredHistory.pending, (state) => {
      state.compositeMergeHistoryLoading = LoadingState.pending;
    });
    builder.addCase(getCompositeMergeTriggeredHistory.fulfilled, (state, { payload }) => {
      state.compositeMergeHistoryLoading = LoadingState.succeed;
      state.compositeMergeHistory = payload;
      if (!!payload.errorMessage) {
        state.compositeMergeErrorMessage = payload.errorMessage;
      }
    });
    builder.addCase(getCompositeMergeTriggeredHistory.rejected, (state) => {
      state.compositeMergeHistoryLoading = LoadingState.failed;
      state.compositeMergeHistory = null;
    }); 
    builder.addCase(addMergeTriggerHistory.pending, (state, { meta})=>{
      state.compositeMergeHistory = {
        createdAt: "", 
        token: meta.arg.token??"", 
        status: EncodingTriggeringStatuses.InProgress, 
        useDatalakeApi: false,
        isStacked: false
      };
      state.compositeMergeHistoryLoading = LoadingState.pending;
    });
    builder.addCase(addMergeTriggerHistory.fulfilled, (state, { payload} )=>{
      state.compositeMergeHistoryLoading = LoadingState.succeed;
      state.compositeMergeHistory = payload;
      if (!!payload.errorMessage) {
        state.compositeMergeErrorMessage = payload.errorMessage;
      }
    });
    builder.addCase(addMergeTriggerHistory.rejected, (state) => {
      state.compositeMergeHistoryLoading = LoadingState.failed;
      state.compositeMergeHistory = null;
    }); 
    
    builder.addCase(getCompositeMergeStatus.fulfilled, (state, { payload, meta})=> {
      state.compositeMergeHistory = payload;
      state.compositeMergeHistoryLoading = LoadingState.succeed;
      if(payload?.status === EncodingTriggeringStatuses.Complete) 
        state.mergeRequestList = [... state.mergeRequestList.filter(x=>x.projectId !== meta.arg.projectId)] ; 
    }); 
  },
});

export const {
  resetcompositeProject,
  setTriggeredEncodingGeneration,
  setLatestVersionLoadingState, 
  setEncodingProgressStage,
  setEncodingBrokenStage,
  setLastMergeRequest
} = compositeProjectSlice.actions;

export const compositeProjectSelector = (state: RootState): CompositeProjectState =>
  state.compositeProject;
 
export const selectLastMergeCommand = createSelector(
  [
    compositeProjectSelector, 
    ( _state: RootState, originProjectId: string)=> originProjectId
  ],
  lastMergeCommandSelector
);

export default compositeProjectSlice.reducer;
